import {
  AssetType,
  AssetWithPresignedUrl,
} from '@droidmap/asset-service-contract'
import {
  AssetCategoryKey,
  AssetSetType,
} from '@droidmap/shared-service-contract'
import {
  assetCategory,
  assetTypesInFacet,
} from '@droidmap/shared-service-utils'

export type { AssetCategoryKey }

export interface AssetCategoryDefinition {
  key: AssetCategoryKey
  label: string
  /** Tailwind colour key for the icon tile (cyan/violet/warning/danger/text). */
  tone: 'accent' | 'violet' | 'warning' | 'danger' | 'success' | 'text'
}

export const ASSET_CATEGORIES: Record<
  AssetCategoryKey,
  AssetCategoryDefinition
> = {
  map: { key: 'map', label: 'Map Assets', tone: 'accent' },
  model: { key: 'model', label: '3D Model', tone: 'violet' },
  imagery: { key: 'imagery', label: 'Imagery', tone: 'accent' },
  data: { key: 'data', label: 'Data', tone: 'warning' },
  documents: { key: 'documents', label: 'Documents', tone: 'danger' },
  other: { key: 'other', label: 'Other', tone: 'text' },
}

/**
 * Raster/COG assets (`.tif`, `.cog`, DSM/DTM) — the Map category.
 *
 * Derived from the shared facet map (#2842) rather than listed again here.
 */
const RASTER_TYPES = new Set<AssetType>(assetTypesInFacet('raster'))

/**
 * Georeferenced raster types only (GeoTIFF / COG / DSM / DTM) — the subset of
 * {@link RASTER_TYPES} the tile service can actually place on the map. Plain
 * `.tif` (`AssetType.Tiff`) is deliberately excluded: ODM texture maps
 * (`odm_textured_model_geo_material*_map_Kd.tif`) are registered as plain
 * TIFFs but carry no CRS, so requesting their `cog_info` 500s the tile service.
 */
export const GEO_RASTER_TYPES: readonly AssetType[] =
  // `raster` minus plain `.tif`, not a facet of its own — the exclusion is the
  // whole point of this list and a facet lookup would quietly restore it.
  assetTypesInFacet('raster').filter((type) => type !== AssetType.Tiff)

const GEO_RASTER_TYPE_SET = new Set<AssetType>(GEO_RASTER_TYPES)

/**
 * True when the asset is a raster/COG (`.tif`, `.cog`, DSM/DTM) — i.e. a
 * tile-able map layer. Used to select the layers for the advanced map view.
 */
export function isRasterAsset(asset: AssetWithPresignedUrl): boolean {
  return RASTER_TYPES.has(asset.assetType)
}

/**
 * True when the asset is a *georeferenced* raster (GeoTIFF / COG / DSM / DTM) —
 * a tile-able map layer the tile service can locate geographically. Unlike
 * {@link isRasterAsset} this excludes plain `.tif` (`AssetType.Tiff`), whose
 * ODM texture maps have no CRS and 500 the tile service's `cog_info`. Use this
 * to pick the layers for the advanced map view.
 */
export function isGeoRasterAsset(asset: { assetType: AssetType }): boolean {
  return GEO_RASTER_TYPE_SET.has(asset.assetType)
}

/**
 * Which category an asset belongs to.
 *
 * Delegates to the shared rule (droidmap#2887). It used to live here, which
 * is why the backend could neither filter nor count by category — #2773 could
 * not page a section and #2774 walked a whole set to find rasters. The
 * categories a query selects and the sections this page renders now come from
 * one definition.
 *
 * Image assets are still routed by the parent set: JPEGs in `odm_outputs` are
 * texture atlases belonging with the 3D model, while JPEGs in `drone_images`
 * are source imagery.
 */
export function categorizeAsset(
  asset: AssetWithPresignedUrl,
  isOdmOutputs: boolean,
): AssetCategoryKey {
  return assetCategory(
    asset.assetType,
    isOdmOutputs ? AssetSetType.OdmOutputs : AssetSetType.DroneImages,
  )
}

export function groupAssetsByCategory(
  assets: AssetWithPresignedUrl[],
  isOdmOutputs: boolean,
): Record<AssetCategoryKey, AssetWithPresignedUrl[]> {
  const groups: Record<AssetCategoryKey, AssetWithPresignedUrl[]> = {
    map: [],
    model: [],
    imagery: [],
    data: [],
    documents: [],
    other: [],
  }
  for (const asset of assets) {
    const key = categorizeAsset(asset, isOdmOutputs)
    groups[key].push(asset)
  }
  return groups
}

export function totalSize(assets: AssetWithPresignedUrl[]): number {
  return assets.reduce((sum, a) => sum + (a.size ?? 0), 0)
}
